import { cn } from '../../utils/cn/cn';

export type CircularProgressProps = {
  /**
   * Additional class names.
   */
  className?: string;
  /**
   * Aria label for the progress indicator.
   * @default 'Content loading'
   */
  ariaLabel?: string;
  /**
   * Value of the progress indicator.
   * @default 0
   */
  value?: number;
  /**
   * The mode of the progress indicator.
   * @default 'indeterminate'
   */
  mode?: 'indeterminate' | 'determinate';
};

const SIZE = 44;
const THICKNESS = 3.6;

/**
 * A circular progress indicator. By default, it is indeterminate.
 */
export const CircularProgress = ({
  className,
  value = 0,
  mode = 'indeterminate',
  ariaLabel = 'Content loading',
}: CircularProgressProps) => {
  const circumference = 2 * Math.PI * ((SIZE - THICKNESS) / 2);

  return (
    <span
      className={cn(
        'icon-emp-spinner relative inline-flex items-center justify-center',
        mode === 'determinate' ? '-rotate-90' : '',
        className,
      )}
      role="progressbar"
      aria-valuenow={mode === 'determinate' ? value : undefined}
    >
      {mode === 'determinate' && (
        <span className="text-color-text-darktransparent text-size-body-sm absolute rotate-90">
          {value}
          <span className="text-sm">%</span>
        </span>
      )}
      <span
        className={`${mode === 'indeterminate' ? 'animate-circular-progress' : ''} absolute h-full w-full`}
      >
        <svg viewBox={`${SIZE / 2} ${SIZE / 2} ${SIZE} ${SIZE}`} aria-label={ariaLabel}>
          <circle
            className={`${mode === 'indeterminate' ? 'animate-circular-progress-circle' : ''} text-color-primary`}
            style={{
              stroke: 'currentcolor',
              strokeDasharray: mode === 'determinate' ? circumference.toFixed(3) : '80px, 200px',
              strokeDashoffset:
                mode === 'determinate'
                  ? `${(((100 - value) / 100) * circumference).toFixed(3)}px`
                  : '0',
              strokeLinecap: 'round',
            }}
            cx={SIZE}
            cy={SIZE}
            r={(SIZE - THICKNESS) / 2}
            fill="none"
            strokeWidth={THICKNESS}
          />
        </svg>
      </span>
      <svg viewBox={`${SIZE / 2} ${SIZE / 2} ${SIZE} ${SIZE}`} aria-label={ariaLabel}>
        <circle
          className="text-color-linear-progress-background"
          style={{
            stroke: 'currentcolor',
            strokeDashoffset: '0',
            strokeLinecap: 'round',
          }}
          cx={SIZE}
          cy={SIZE}
          r={(SIZE - THICKNESS) / 2}
          fill="none"
          strokeWidth={THICKNESS}
        />
      </svg>
    </span>
  );
};
